import React, { useState, useRef } from "react"
import Bookmark from "../../../../components/bookmark/bookmark"
import { FormattedMessage } from "react-intl"
import { WingsButton } from "../buttons"
import { WingsHeading, WingsLink, WingsDetailText } from "../typography"
import Link from "../../../../link"
import ReactPlayer from "react-player"
import ThumbsFeedback from "../../../../components/assessment/thumbs-feedback/ThumbsFeedback"
import { resolveCssVariable, pickAccessibleTextColor } from "../../../../utils"
import "./WingsCardContainer.css"

/**
 * Container component that renders a card's image, headers, and bookmark. Not
 * currently exported outside of wingspans-ui.
 * @prop {string} imageUrl - parent is expected to pass default image if necessary
 * @prop {string} imageAlt
 * @prop {string} title
 * @prop {string} to - same as to prop of Link component
 * @prop {string} subtitle
 * @prop {string} className
 * @prop {boolean} hideBookmark - whether to display a bookmark
 * @prop {string} objectId - <slug>-<locale>, used for bookmarking
 * @prop {string} slug - used for bookmarking
 * @prop {string} contentType - used for bookmarking
 * @prop {Object} jobCategories - used for bookmarking careers
 * @prop {*[]} careerPathwaysFilter - used for bookmarking careers
 * @prop {string} leftLabel - if provided, appears in bottom left of image
 * @prop {string} leftLabelColor - css color of left label
 * @prop {string} rightLabel - if provided, appears in bottom right of image
 * @prop {string} rightLabelColor - css color of right label
 * @prop {Function} onDislike - If provided, thumbs up/down buttons will be rendered. Runs when the object is disliked with it's slug as an argument
 * @prop {string} imageButtonLabel - If included, button with this text will be rendered within card image
 * @prop {Function} onImageButtonClick - Runs when image section or button within is clicked
 * @returns {JSX}
 */
const WingsCardContainer = ({
  imageUrl,
  imageAlt,
  videoUrl,
  title,
  to,
  subtitle,
  className = "",
  objectID,
  slug,
  contentType,
  jobCategories,
  careerPathwaysFilter,
  hideBookmark = false,
  leftLabel,
  leftLabelColor = "var(--w-color-pink-500)",
  rightLabel,
  rightLabelColor = "var(--w-color-orange-500)",
  onDislike,
  imageButtonLabel,
  onImageButtonClick,
  children,
}) => {
  const [imageSectionHovered, setImageSectionHovered] = useState(false)
  const watchVideoButtonRef = useRef(null)

  const imageSectionHasButton = videoUrl || imageButtonLabel
  const ImageSectionWrapper = (!to || imageSectionHasButton) ? "div" : Link
  const imageSectionEventHandlers = {}
  if (imageSectionHasButton) {
    imageSectionEventHandlers.onPointerEnter = () => setImageSectionHovered(true)
    imageSectionEventHandlers.onPointerLeave = () => setImageSectionHovered(false)
    imageSectionEventHandlers.onClick = (e) => {
      if (watchVideoButtonRef.current) {
        watchVideoButtonRef.current.click()
      } else if (onImageButtonClick) {
        onImageButtonClick(e)
      }
    }
  }

  let leftLabelTextColor = "#FFFFFF"
  if (leftLabel) {
    leftLabelTextColor = pickAccessibleTextColor(resolveCssVariable(leftLabelColor) || "#000000")
  }
  let rightLabelTextColor = "#FFFFFF"
  if (rightLabel) {
    rightLabelTextColor = pickAccessibleTextColor(resolveCssVariable(rightLabelColor) || "#000000")
  }

  return (
    <div className={`card-container w-flex w-flex-column w-items-center w-gap-1 ${className}`}>
      <ImageSectionWrapper
        className={`card-image-container w-full w-rounded ${imageSectionHasButton ? "card-image-container-with-button" : ""}`}
        to={to}
        {...imageSectionEventHandlers}
      >
        <img
          src={imageUrl}
          alt={imageAlt || `${contentType} card image`}
          className="card-image"
        />
        {videoUrl ? (
          <div className="card-video-wrapper">
            <ReactPlayer
              url={videoUrl}
              light={imageUrl}
              controls={true}
              playing={true}
              width="100%"
              height="100%"
              playIcon={
                <WingsButton
                  className="card-video-button"
                  size="mini"
                  variant="tal-e"
                  iconName="video"
                  forceHoveredStyle={imageSectionHovered}
                  buttonRef={watchVideoButtonRef}
                >
                  <FormattedMessage id="wings-card-watch-video" defaultMessage="Watch Video" />
                </WingsButton>
              }
            />
          </div>
        ) : imageButtonLabel ? (
          <WingsButton
            className="card-image-section-button"
            variant="secondary"
            size="small"
            onClick={onImageButtonClick}
            iconName="career-plans-solid"
            forceHoveredStyle={imageSectionHovered}
          >
            {imageButtonLabel}
          </WingsButton>
        ) : null}
        {(leftLabel || rightLabel) && (
          <div className="card-image-labels-container w-flex w-full w-flex-row w-items-center w-justify-between">
            {leftLabel && (
              <div
                className="card-image-label w-flex w-items-center w-justify-center w-px-1"
                style={{color: leftLabelTextColor, backgroundColor: leftLabelColor}}
              >
                <WingsDetailText className="w-font-bold w-color-white">
                  {leftLabel}
                </WingsDetailText>
              </div>
            )}
            {rightLabel && (
              <div
                className="card-image-label w-ml-auto w-flex w-items-center w-justify-center w-px-1"
                style={{color: rightLabelTextColor, backgroundColor: rightLabelColor}}
              >
                <WingsDetailText className="w-font-bold">
                  {rightLabel}
                </WingsDetailText>
              </div>
            )}
          </div>
        )}
      </ImageSectionWrapper>
      <div className="card-header-section w-flex w-flex-row w-items-start w-justify-between w-gap-1">
        <div className="w-flex w-flex-column w-items-start w">
          {subtitle && (
            <WingsHeading level={6}>
              {subtitle}
            </WingsHeading>
          )}
          {to ? (
            <WingsLink to={to} size="h5" display="block">
              {title}
            </WingsLink>
          ) : (
            <WingsHeading level={5}>
              {title}
            </WingsHeading>
          )}
        </div>
        {(onDislike || !hideBookmark) && (
          <div className="card-buttons-container w-flex w-flex-row w-items-center w-justify-end">
            {onDislike && (
              <ThumbsFeedback
                contentId={objectID}
                contentSlug={slug}
                onDislike={onDislike}
              />
            )}
            {!hideBookmark && (
              <Bookmark
                contentTitle={title}
                classContext={"card"}
                contentId={objectID}
                contentSlug={slug}
                contentType={contentType}
                jobCategories={jobCategories}
                careerPathwaysFilter={careerPathwaysFilter}
              />
            )}
          </div>
        )}
      </div>
      {children && (
        <div className="card-content-section w-flex w-flex-column w-justify-start w-items-start">
          {children}
        </div>
      )}
    </div>
  )
}

export default WingsCardContainer
